/**
 * UserJobsTab Component
 * Display paginated table of user's processing jobs
 */

import { useState, useEffect } from "react";
import { db } from "@/lib/instantdb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Film,
  Inbox,
} from "lucide-react";
import { JobDetailSheet } from "./JobDetailSheet";

// Types based on InstantDB schema
interface ProcessingJob {
  id: string;
  projectId?: string;
  status?: string;
  progress?: number;
  message?: string;
  startedAt?: number;
  completedAt?: number;
  outputR2Key?: string;
  error?: string;
  analyticsJson?: Record<string, unknown>;
  edlJson?: Record<string, unknown>;
}

interface ProjectVideo {
  id: string;
  projectId?: string;
  r2Key?: string;
  fileName?: string;
  fileSize?: number;
  duration?: number;
  uploadStatus?: string;
}

interface Project {
  id: string;
  name: string;
  userId: string;
  status?: string;
  thumbnail?: string;
  jobs?: ProcessingJob[];
  videos?: ProjectVideo[];
}

interface UserJobsTabProps {
  userId: string;
  userEmail?: string;
}

const PAGE_SIZE = 10;

const statusColors: Record<string, string> = {
  queued: "bg-gray-500",
  uploading: "bg-blue-500",
  analyzing: "bg-blue-500",
  rendering: "bg-yellow-500",
  completed: "bg-green-500",
  failed: "bg-red-500",
};

const statusLabels: Record<string, string> = {
  queued: "Queued",
  uploading: "Uploading",
  analyzing: "Analyzing",
  rendering: "Rendering",
  completed: "Completed",
  failed: "Failed",
};

function formatDateTime(timestamp?: number): string {
  if (!timestamp) return "—";
  return new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function UserJobsTab({ userId, userEmail }: UserJobsTabProps) {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [allJobs, setAllJobs] = useState<
    Array<{ job: ProcessingJob; project: Project; videos: ProjectVideo[] }>
  >([]);
  const [page, setPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState<{
    job: ProcessingJob;
    project: Project;
    videos: ProjectVideo[];
  } | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [appUserId, setAppUserId] = useState<string | null>(null);

  // Fetch user's jobs by querying processing_jobs with project relation
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // First, try to find the app user by email (admin_users and $users are separate tables)
        let targetUserIds: string[] = [userId];

        if (userEmail) {
          const { data: usersData } = await db.queryOnce({
            $users: {
              $: { where: { email: userEmail } },
            },
          });

          const appUsers = usersData?.$users ?? [];
          if (appUsers.length > 0) {
            // Found an app user with matching email
            const foundUserId = (appUsers[0] as { id: string }).id;
            setAppUserId(foundUserId);
            // Include both IDs in our search (in case they're different)
            if (foundUserId !== userId) {
              targetUserIds.push(foundUserId);
            }
          }
        }

        // Query ALL processing_jobs with their project relation
        // Then filter client-side by project.userId
        const { data } = await db.queryOnce({
          processing_jobs: {
            $: {
              order: { serverCreatedAt: "desc" },
              limit: 500,
            },
            project: {
              videos: {},
            },
          },
        });

        type JobWithProject = {
          id: string;
          projectId?: string;
          status?: string;
          progress?: number;
          message?: string;
          startedAt?: number;
          completedAt?: number;
          outputR2Key?: string;
          error?: string;
          analyticsJson?: Record<string, unknown>;
          edlJson?: Record<string, unknown>;
          userId?: string;
          project?: Array<{
            id: string;
            userId: string;
            name: string;
            videos?: ProjectVideo[];
          }>;
        };

        const allJobs = (data?.processing_jobs ?? []) as JobWithProject[];

        // Helper to get project data (handles both array and object format)
        const getProjectData = (job: JobWithProject) => {
          if (Array.isArray(job.project)) {
            return job.project[0];
          }
          return job.project as unknown as { id: string; userId: string; name: string; videos?: ProjectVideo[] } | undefined;
        };

        // Debug: log all unique userIds we see on projects
        const seenUserIds = new Set<string>();
        for (const job of allJobs) {
          const projectData = getProjectData(job);
          if (projectData?.userId) {
            seenUserIds.add(projectData.userId);
          }
          if (job.userId) {
            seenUserIds.add(job.userId);
          }
        }
        console.log("[UserJobsTab] Target user IDs:", targetUserIds);
        console.log("[UserJobsTab] All unique userIds on jobs/projects:", Array.from(seenUserIds));
        console.log("[UserJobsTab] Total jobs fetched:", allJobs.length);

        // Filter jobs by project.userId OR job.userId matching target user IDs
        const userJobs = allJobs.filter((job) => {
          const projectData = getProjectData(job);
          const projectUserId = projectData?.userId;
          const jobUserId = job.userId;
          return (
            targetUserIds.includes(projectUserId || "") ||
            targetUserIds.includes(jobUserId || "")
          );
        });

        console.log("[UserJobsTab] Filtered jobs for user:", userJobs.length);
        console.log("[UserJobsTab] First filtered job:", userJobs[0]);
        console.log("[UserJobsTab] First job's project:", userJobs[0]?.project);

        // Collect unique projects
        const projectMap = new Map<string, Project>();
        for (const job of userJobs) {
          // Check both array format and direct object format for project
          const projectData = Array.isArray(job.project) ? job.project[0] : job.project;
          if (projectData && typeof projectData === 'object' && 'id' in projectData) {
            const p = projectData as { id: string; userId: string; name: string; videos?: ProjectVideo[] };
            if (!projectMap.has(p.id)) {
              projectMap.set(p.id, {
                id: p.id,
                userId: p.userId,
                name: p.name,
                videos: p.videos,
              });
            }
          }
        }
        console.log("[UserJobsTab] Unique projects found:", projectMap.size);
        setProjects(Array.from(projectMap.values()));

        // Build jobs list with project and video references
        const jobsList: Array<{
          job: ProcessingJob;
          project: Project;
          videos: ProjectVideo[];
        }> = [];

        for (const job of userJobs) {
          // Check both array format and direct object format for project
          const projectData = Array.isArray(job.project) ? job.project[0] : job.project;
          if (projectData && typeof projectData === 'object' && 'id' in projectData) {
            const p = projectData as { id: string; userId: string; name: string; videos?: ProjectVideo[] };
            jobsList.push({
              job: {
                id: job.id,
                projectId: job.projectId,
                status: job.status,
                progress: job.progress,
                message: job.message,
                startedAt: job.startedAt,
                completedAt: job.completedAt,
                outputR2Key: job.outputR2Key,
                error: job.error,
                analyticsJson: job.analyticsJson,
                edlJson: job.edlJson,
              },
              project: {
                id: p.id,
                userId: p.userId,
                name: p.name,
              },
              videos: (p.videos ?? []) as ProjectVideo[],
            });
          }
        }

        console.log("[UserJobsTab] Final jobs list length:", jobsList.length);

        // Sort by startedAt descending (most recent first)
        jobsList.sort((a, b) => {
          const aTime = a.job.startedAt || 0;
          const bTime = b.job.startedAt || 0;
          return bTime - aTime;
        });

        setAllJobs(jobsList);
      } catch (error) {
        console.error("Failed to load user jobs:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userId, userEmail]);

  const totalJobs = allJobs.length;
  const totalPages = Math.ceil(totalJobs / PAGE_SIZE);
  const paginatedJobs = allJobs.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const handleRowClick = (jobData: {
    job: ProcessingJob;
    project: Project;
    videos: ProjectVideo[];
  }) => {
    setSelectedJob(jobData);
    setSheetOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border">
          <div className="p-4">
            <Skeleton className="h-8 w-48 mb-4" />
          </div>
          <div className="border-t">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-4 border-b last:border-0">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (totalJobs === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-muted p-4 mb-4">
          <Inbox className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium">No Jobs Found</h3>
        <p className="text-sm text-muted-foreground mt-1">
          No video processing jobs found for this user.
        </p>
        <div className="text-xs text-muted-foreground mt-4 space-y-1">
          <p>Admin User ID: {userId.slice(0, 8)}...</p>
          {appUserId && appUserId !== userId && (
            <p>App User ID: {appUserId.slice(0, 8)}...</p>
          )}
          {!appUserId && userEmail && (
            <p>No app user found for email: {userEmail}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-1">
          <Film className="h-4 w-4" />
          <span>{projects.length} project(s)</span>
        </div>
        <div className="flex items-center gap-1">
          <span>{totalJobs} job(s)</span>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Project</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Completed</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedJobs.map(({ job, project, videos }) => {
              const status = job.status || "queued";
              const isInProgress = ["queued", "uploading", "analyzing", "rendering"].includes(status);

              return (
                <TableRow
                  key={job.id}
                  className="cursor-pointer"
                  onClick={() => handleRowClick({ job, project, videos })}
                >
                  <TableCell className="font-medium max-w-[200px] truncate">
                    {project.name}
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={`${statusColors[status] || "bg-gray-500"} text-white text-xs`}
                    >
                      {statusLabels[status] || status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {isInProgress ? (
                      <div className="flex items-center gap-2 min-w-[100px]">
                        <Progress value={job.progress || 0} className="h-2 flex-1" />
                        <span className="text-xs text-muted-foreground w-8">
                          {job.progress || 0}%
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">
                        {status === "completed" ? "100%" : "—"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDateTime(job.startedAt)}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDateTime(job.completedAt)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick({ job, project, videos });
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * PAGE_SIZE + 1} to{" "}
            {Math.min(page * PAGE_SIZE, totalJobs)} of {totalJobs} jobs
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground px-2">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Job Detail Sheet */}
      <JobDetailSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        job={selectedJob?.job || null}
        project={selectedJob?.project}
        sourceVideos={selectedJob?.videos}
      />
    </div>
  );
}
